import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import html2pdf from 'html2pdf.js';
import '../../../component-styles/Proposal-UI.css';
import '../../../component-styles/PurchaseOrderForm.css';
import notify from '../../../utils/notify';
import useUnsavedChangesGuard from '../../../hooks/useUnsavedChangesGuard';
import ConfirmModal from '../../utils/ConfirmModal';

import HeaderSection from '../Proposals/ProposalForm/HeaderSection';
import { DOCUMENT_TITLE_MAX_LENGTH } from '../../../utils/documentTitle.js';
import CatalogSection from '../Proposals/ProposalForm/CatalogSection';
import TagSelector from '../../shared/TagSelector';
import PurchaseOrderPayments from './PurchaseOrderPayments';

import PurchaseOrderAttachments from './PurchaseOrderAttachments';
import LineItemCard from './LineItems/POLineItemCard';
import AddItemForm from '../../../components/ProductsServices/AddItemForm';
import API_BASE from '../../../utils/api.js';
import usePermissions from '../../../auth/usePermissions';
import AddressAutocomplete from '../../common/AddressAutocomplete';
import QuickBooksConnectModal from '../../utils/QuickbooksConnectModal';
import { isQuickBooksConnectionError } from '../../../utils/quickbooksErrors';
import {
  autoCreateQuickBooksPurchaseOrder,
  autoUpdateQuickBooksPurchaseOrder,
  shouldAutoCreateInQuickBooks,
} from '../../../utils/quickbooksAutoSync.js';
import {
  getFreightDestinationOptions,
  getFreightDestinationAddress,
  isWillCallDestination,
} from '../../../utils/freightDestinations.js';
import { buildFirmBillTo, parseCompanyAddress } from '../../../utils/companyAddress.js';
import { BUILT_IN_PO_STATUSES, normalizeCustomStatuses } from '../../../utils/orderStatuses.js';
import StatusSelect from '../../shared/StatusSelect';
import SendPurchaseOrderModal from './SendPurchaseOrderModal';
import { buildPublicPurchaseOrderVendorUrl, buildPublicPurchaseOrderClientUrl } from '../../../utils/publicLinks.js';

/* ----------------------------------------------------
   UTILITIES
----------------------------------------------------- */

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmt2 = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : '0.00';
};

const fmtMoney = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '$0.00';
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
};

const fmtDate = (value) => {
  if (!value) return '';
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
};

const splitLines = (value) =>
  String(value || '')
    .split(/\r?\n|,/)
    .map((part) => part.trim())
    .filter(Boolean);

const genId = () => `itm_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const proxyImg = (url) => {
  if (!url) return url;
  return `${API_BASE}/api/upload/proxy-image?url=${encodeURIComponent(url)}`;
};

const getQuickBooksPurchaseOrderUrl = (purchaseOrderId, env = 'sandbox') => {
  const base = env === 'production' ? 'https://qbo.intuit.com' : 'https://sandbox.qbo.intuit.com';
  return `${base}/app/purchaseorder?txnId=${purchaseOrderId}`;
};

const getQuickBooksBillUrl = (billId, env = 'sandbox') => {
  const base = env === 'production' ? 'https://qbo.intuit.com' : 'https://sandbox.qbo.intuit.com';
  return `${base}/app/bill?txnId=${billId}`;
};

/* ----------------------------------------------------
   MAIN COMPONENT
----------------------------------------------------- */

export default function PurchaseOrderForm({
  onClose,
  onSaved,
  project,
  initial,
  autoDownload,
  queueRemaining = 0,
  queuePosition = null,
  queueTotal = null,
}) {
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const canEdit = permissions.createEditPurchaseOrders !== false;

  const userId = localStorage.getItem('userId');
  const pdfDocumentRef = useRef(null);
  const resolveTaxRatePct = (value) => {
    if (value == null || value === '') return '';
    if (typeof value === 'number') return value > 1 ? value : value * 100;
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return '';
    return parsed > 1 ? parsed : parsed * 100;
  };
  const [taxLocations, setTaxLocations] = useState([]);
  const [quickbooksSettings, setQuickbooksSettings] = useState(null);
  const [defaultShippingDestination, setDefaultShippingDestination] = useState('designer_warehouse');
  const [freightDestinations, setFreightDestinations] = useState([]);
  const [customOrderStatuses, setCustomOrderStatuses] = useState([]);
  const [company, setCompany] = useState({
    companyName: '',
    address: '',
    contactNumber: '',
    email: '',
    website: '',
    logoUrl: '',
  });
  // The PDF header (company name, address, logo) comes from settings, so an
  // auto-download has to wait for this request rather than a fixed delay.
  const [settingsLoaded, setSettingsLoaded] = useState(!userId);

  useEffect(() => {
    if (!userId) return;

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/settings/${userId}`);
        if (!res.ok) return;

        const settings = await res.json();
        setQuickbooksSettings(settings?.quickbooks || null);
        setDefaultShippingDestination(settings?.defaultShippingDestination || 'designer_warehouse');
        setFreightDestinations(Array.isArray(settings?.freightDestinations) ? settings.freightDestinations : []);
        setCustomOrderStatuses(
          Array.isArray(settings?.customOrderStatuses) ? settings.customOrderStatuses : []
        );
        const normalized = (settings.taxLocations || []).map((t, idx) => {
          const rate =
            typeof t.tax === 'string' ? Number(t.tax.replace('%', '')) / 100 : Number(t.rate);

          return {
            id: t.id || `tax-${idx}`,
            rate: Number.isFinite(rate) ? rate : 0,
          };
        });

        setTaxLocations(normalized);
        setCompany({
          companyName: settings.company?.companyName || '',
          address: settings.company?.address || '',
          contactNumber: settings.company?.contactNumber || '',
          email: settings.company?.email || '',
          website: settings.company?.website || '',
          logoUrl: settings.company?.logoUrl || '',
        });
      } catch (err) {
        console.error('Failed to load tax settings', err);
      } finally {
        setSettingsLoaded(true);
      }
    })();
  }, [userId]);

  const [schedules, setSchedules] = useState([]);
  const projectId = project?.id ?? project?.projectId;

  useEffect(() => {
    if (!userId || !projectId) return;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/schedules/${encodeURIComponent(userId)}/${encodeURIComponent(projectId)}`,
          { cache: 'no-store' }
        );
        const data = await res.json();
        setSchedules(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to fetch schedules:', err);
      }
    })();
  }, [userId, projectId]);

  const resolvedProjectTaxRatePct = useMemo(() => {
    const salesTaxRate = project?.salesTaxRate;
    if (salesTaxRate == null || salesTaxRate === '') return '';

    const match = taxLocations.find((t) => t.id === salesTaxRate);
    if (match) return resolveTaxRatePct(match.rate);

    return resolveTaxRatePct(salesTaxRate);
  }, [project?.salesTaxRate, taxLocations]);

  /* ----------------------------------------------------
     FORM STATE
  ----------------------------------------------------- */

  const [form, setForm] = useState(() => ({
    orderNumber: initial?.orderNumber || '',
    title: initial?.title || '',
    date: initial?.date || new Date().toISOString().split('T')[0],
    status: initial?.status || 'Draft',
    sidemark: initial?.sidemark || project?.projectNickname || project?.name || '',
    notes: initial?.notes || '',
    trackingUrl: initial?.trackingUrl || '',

    vendorName: initial?.vendorName || '',
    vendorId: initial?.vendorId || '',
    vendorStreet: initial?.vendorStreet || '',
    vendorCity: initial?.vendorCity || '',
    vendorState: initial?.vendorState || '',
    vendorZip: initial?.vendorZip || '',
    vendorPhone: initial?.vendorPhone || '',
    vendorEmail: initial?.vendorEmail || '',

    billToName:
      initial?.billToName || '',
    billToStreet: initial?.billToStreet || '',
    billToCity: initial?.billToCity || '',
    billToState: initial?.billToState || '',
    billToZip: initial?.billToZip || '',
    billToEmail: initial?.billToEmail || '',
    billToPhone: initial?.billToPhone || '',

    shipToType: initial?.shipToType || 'designer_warehouse',
    shipCompany: initial?.shipCompany || project?.receivingCompany || '',
    shipStreet: initial?.shipStreet || project?.receivingStreet || '',
    shipCity: initial?.shipCity || project?.receivingCity || '',
    shipState: initial?.shipState || project?.receivingState || '',
    shipZip: initial?.shipZip || project?.receivingZip || '',
    shipEmail: initial?.shipEmail || project?.receivingEmail || '',
    shipPhone: initial?.shipPhone || project?.receivingPhone || '',

    freight: initial?.freight ?? '',
    tariff: initial?.tariff ?? '',
    taxes: initial?.taxes ?? '',
    payments: initial?.payments ?? '',
    taxRatePct: initial?.taxRatePct ?? resolvedProjectTaxRatePct,
    depositRequestedPct: initial?.depositRequestedPct ?? 50,
    vendorPaymentTerms: initial?.vendorPaymentTerms ?? '',
    depositDueDate: initial?.depositDueDate ?? '',
    balanceDueDate: initial?.balanceDueDate ?? '',
    estimatedDeliveryDate: initial?.estimatedDeliveryDate ?? '',
    confirmedShipDate: initial?.confirmedShipDate ?? '',
    actualDeliveryDate: initial?.actualDeliveryDate ?? '',
    attachments: initial?.attachments || [],
  }));

  useEffect(() => {
    if (!initial) return;
    if (initial.tags) setTags(initial.tags);
    setForm((prev) => ({
      ...prev,
      orderNumber: initial.orderNumber || prev.orderNumber,
      title: initial.title ?? prev.title,
      date: initial.date || prev.date,
      status: initial.status || prev.status,
      sidemark: initial.sidemark || prev.sidemark,
      notes: initial.notes || prev.notes,
      trackingUrl: initial.trackingUrl || prev.trackingUrl,
      vendorName: initial.vendorName || prev.vendorName,
      vendorId: initial.vendorId || prev.vendorId,
      vendorStreet: initial.vendorStreet || prev.vendorStreet,
      vendorCity: initial.vendorCity || prev.vendorCity,
      vendorState: initial.vendorState || prev.vendorState,
      vendorZip: initial.vendorZip || prev.vendorZip,
      vendorPhone: initial.vendorPhone || prev.vendorPhone,
      vendorEmail: initial.vendorEmail || prev.vendorEmail,
      billToName: initial.billToName || prev.billToName,
      billToStreet: initial.billToStreet || prev.billToStreet,
      billToCity: initial.billToCity || prev.billToCity,
      billToState: initial.billToState || prev.billToState,
      billToZip: initial.billToZip || prev.billToZip,
      billToEmail: initial.billToEmail || prev.billToEmail,
      billToPhone: initial.billToPhone || prev.billToPhone,
      shipCompany: initial.shipCompany || prev.shipCompany,
      shipStreet: initial.shipStreet || prev.shipStreet,
      shipCity: initial.shipCity || prev.shipCity,
      shipState: initial.shipState || prev.shipState,
      shipZip: initial.shipZip || prev.shipZip,
      shipEmail: initial.shipEmail || prev.shipEmail,
      shipPhone: initial.shipPhone || prev.shipPhone,
      freight: initial.freight ?? prev.freight,
      tariff: initial.tariff ?? prev.tariff,
      taxes: initial.taxes ?? prev.taxes,
      payments: initial.payments ?? prev.payments,
      taxRatePct: initial.taxRatePct ?? prev.taxRatePct ?? resolvedProjectTaxRatePct,
      depositRequestedPct: initial.depositRequestedPct ?? prev.depositRequestedPct ?? 50,
      vendorPaymentTerms: initial.vendorPaymentTerms ?? prev.vendorPaymentTerms,
      depositDueDate: initial.depositDueDate ?? prev.depositDueDate,
      balanceDueDate: initial.balanceDueDate ?? prev.balanceDueDate,
      estimatedDeliveryDate: initial.estimatedDeliveryDate ?? prev.estimatedDeliveryDate,
      confirmedShipDate: initial.confirmedShipDate ?? prev.confirmedShipDate,
      actualDeliveryDate: initial.actualDeliveryDate ?? prev.actualDeliveryDate,
      attachments: initial.attachments || prev.attachments,
    }));
  }, [initial, resolvedProjectTaxRatePct]);

  useEffect(() => {
    if (initial?.taxRatePct != null) return;
    if (resolvedProjectTaxRatePct === '') return;

    setForm((prev) => {
      if (prev.taxRatePct != null && prev.taxRatePct !== '') return prev;
      return { ...prev, taxRatePct: resolvedProjectTaxRatePct };
    });
  }, [initial?.taxRatePct, resolvedProjectTaxRatePct]);

  const BLANK_SHIP_TO = {
    shipCompany: '',
    shipStreet: '',
    shipCity: '',
    shipState: '',
    shipZip: '',
    shipEmail: '',
    shipPhone: '',
  };

  // Resolves the Ship To block for a freight destination, most specific source
  // first: the address saved on the destination in Settings, then the project's
  // own receiver, then the firm's address. Returns null when nothing is known,
  // so callers can decide between leaving the block alone and clearing it.
  const shipToAddressFor = (shipToType) => {
    if (isWillCallDestination(shipToType)) return { ...BLANK_SHIP_TO };

    const saved = getFreightDestinationAddress(shipToType, freightDestinations);
    if (saved) {
      return {
        shipCompany: saved.company,
        shipStreet: saved.street,
        shipCity: saved.city,
        shipState: saved.state,
        shipZip: saved.zip,
        shipEmail: saved.email,
        shipPhone: saved.phone,
      };
    }

    if (shipToType === 'client_site' && project?.projectStreet) {
      return {
        shipCompany:
          project?.customerName ||
          `${project?.customerFirst || ''} ${project?.customerLast || ''}`.trim() ||
          '',
        shipStreet: project.projectStreet || '',
        shipCity: project.projectCity || '',
        shipState: project.projectState || '',
        shipZip: project.projectZip || '',
        shipEmail: project?.email || '',
        shipPhone: project?.phone || '',
      };
    }

    if (project?.receivingStreet) {
      return {
        shipCompany: project.receivingCompany || '',
        shipStreet: project.receivingStreet || '',
        shipCity: project.receivingCity || '',
        shipState: project.receivingState || '',
        shipZip: project.receivingZip || '',
        shipEmail: project.receivingEmail || '',
        shipPhone: project.receivingPhone || '',
      };
    }

    if (shipToType === 'designer_warehouse' && (company.companyName || company.address)) {
      const { street, city, state, zip } = parseCompanyAddress(company.address);
      return {
        shipCompany: company.companyName || '',
        shipStreet: street,
        shipCity: city,
        shipState: state,
        shipZip: zip,
        shipEmail: company.email || '',
        shipPhone: company.contactNumber || '',
      };
    }

    return null;
  };

  // Switching destination replaces the whole Ship To block — leaving the
  // previous destination's address behind is how a PO ends up shipping to
  // the wrong place.
  const applyShipToType = (shipToType) => {
    setForm((f) => ({
      ...f,
      shipToType,
      ...(shipToAddressFor(shipToType) || BLANK_SHIP_TO),
    }));
  };

  const willCall = isWillCallDestination(form.shipToType);

  // A PO is billed to the firm, so Bill To defaults to Settings > Company —
  // split into street/city/state/zip rather than dumped whole into Street.
  useEffect(() => {
    if (initial) return;
    const firmBillTo = buildFirmBillTo(company);
    const shipToType = defaultShippingDestination || 'designer_warehouse';
    const savedShipTo = shipToAddressFor(shipToType);

    setForm((prev) => ({
      ...prev,
      billToName: prev.billToName || firmBillTo.billToName,
      billToStreet: prev.billToStreet || firmBillTo.billToStreet,
      billToCity: prev.billToCity || firmBillTo.billToCity,
      billToState: prev.billToState || firmBillTo.billToState,
      billToZip: prev.billToZip || firmBillTo.billToZip,
      billToEmail: prev.billToEmail || firmBillTo.billToEmail,
      billToPhone: prev.billToPhone || firmBillTo.billToPhone,
      shipToType: prev.shipToType || shipToType,
      ...(savedShipTo && {
        shipCompany: prev.shipCompany || savedShipTo.shipCompany,
        shipStreet: prev.shipStreet || savedShipTo.shipStreet,
        shipCity: prev.shipCity || savedShipTo.shipCity,
        shipState: prev.shipState || savedShipTo.shipState,
        shipZip: prev.shipZip || savedShipTo.shipZip,
        shipEmail: prev.shipEmail || savedShipTo.shipEmail,
        shipPhone: prev.shipPhone || savedShipTo.shipPhone,
      }),
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, company, defaultShippingDestination, freightDestinations, project]);

  const [savedPO, setSavedPO] = useState(initial || null);
  const [tags, setTags] = useState(initial?.tags || []);
  const qbPurchaseOrder = savedPO?.quickbooks || initial?.quickbooks || null;
  const qbBillUrl = qbPurchaseOrder?.billId
    ? getQuickBooksBillUrl(qbPurchaseOrder.billId, qbPurchaseOrder.environment)
    : null;
  const qbCompanionPoUrl = qbPurchaseOrder?.purchaseOrderId
    ? getQuickBooksPurchaseOrderUrl(qbPurchaseOrder.purchaseOrderId, qbPurchaseOrder.environment)
    : null;

  useEffect(() => {
    if (initial) setSavedPO(initial);
  }, [initial]);

  /* ----------------------------------------------------
     LINE ITEMS (COST ONLY)
  ----------------------------------------------------- */

  const [items, setItems] = useState(() =>
    (initial?.items || []).map((it) => ({
      itemId: it.itemId || genId(),
      productItemId: it.productItemId ?? it.itemId ?? null,
      vendorId: it.vendorId || null,
      type: it.type || '',
      category: it.category || '',
      salesCode: it.salesCode || '',
      name: it.name || '',
      description: it.description || '',
      sourceDescription: it.sourceDescription || '',
      imageUrl: it.imageUrl || '',
      vendorName: it.vendorName || '',
      room: it.room || '',
      scheduleId: it.scheduleId || '',
      rate: num(it.rate),
      shipping: num(it.shipping ?? 0),
      quantity: num(it.quantity),
      unitType: it.unitType || 'Each',
      shipToType: it.shipToType || '',
      shipToAddress: it.shipToAddress || '',
      status: it.status || 'Draft',
      orderedDate: it.orderedDate || '',
      backorderedDate: it.backorderedDate || '',
      enRouteDate: it.enRouteDate || '',
      freightTracking: it.freightTracking || '',
      receivedDate: it.receivedDate || '',
      warehouseReceiptNumber: it.warehouseReceiptNumber || '',
      deliveredDate: it.deliveredDate || '',
      installedDate: it.installedDate || '',
      source: 'purchase_order',
    }))
  );

  useEffect(() => {
    if (!initial?.items) return;
    setItems(
      initial.items.map((it) => ({
        itemId: it.itemId || genId(),
        productItemId: it.productItemId ?? it.itemId ?? null,
        vendorId: it.vendorId || null,
        type: it.type || '',
        category: it.category || '',
        salesCode: it.salesCode || '',
        name: it.name || '',
        description: it.description || '',
        sourceDescription: it.sourceDescription || '',
        imageUrl: it.imageUrl || '',
        vendorName: it.vendorName || '',
        room: it.room || '',
        scheduleId: it.scheduleId || '',
        rate: num(it.rate),
        shipping: num(it.shipping ?? 0),
        quantity: num(it.quantity),
        unitType: it.unitType || 'Each',
        shipToType: it.shipToType || '',
        shipToAddress: it.shipToAddress || '',
        status: it.status || 'Draft',
        orderedDate: it.orderedDate || '',
        backorderedDate: it.backorderedDate || '',
        enRouteDate: it.enRouteDate || '',
        freightTracking: it.freightTracking || '',
        receivedDate: it.receivedDate || '',
        warehouseReceiptNumber: it.warehouseReceiptNumber || '',
        deliveredDate: it.deliveredDate || '',
        installedDate: it.installedDate || '',
        source: 'purchase_order',
      }))
    );
  }, [initial]);

  const addItemFromCatalog = (it) => {
    setItems((prev) => [
      ...prev,
      {
        itemId: genId(),
        productItemId: it.itemId || it.productItemId || null,
        vendorId: it.vendorId || null,
        type: it.type || '',
        category: it.category || '',
        salesCode: it.salesCode || '',
        name: it.name || '',
        description: it.description || '',
        imageUrl: it.imageUrl || '',
        vendorName: it.vendorName || '',
        room: it.room || '',
        scheduleId: it.scheduleId || '',
        rate: num(it.tradePrice ?? it.price ?? 0),
        shipping: num(it.shipping ?? 0),
        quantity: 1,
        unitType: it.unitType || 'Each',
        shipToType: form.shipToType || defaultShippingDestination || 'designer_warehouse',
        shipToAddress: '',
        status: 'Draft',
        source: 'purchase_order',
      },
    ]);

    if (!form.vendorName && it.vendorName) {
      setForm((f) => ({
        ...f,
        vendorName: it.vendorName,
        vendorId: it.vendorId || f.vendorId || '',
      }));
    }
  };

  // A blank, fully editable line for charges that aren't catalog items —
  // handling, freight, crating, a workroom fee the vendor bills separately.
  const addCustomLineItem = () => {
    setItems((prev) => [
      ...prev,
      {
        itemId: genId(),
        productItemId: null,
        vendorId: form.vendorId || null,
        type: 'service',
        category: '',
        salesCode: '',
        name: '',
        description: '',
        imageUrl: '',
        vendorName: form.vendorName || '',
        room: '',
        scheduleId: '',
        rate: 0,
        shipping: 0,
        quantity: 1,
        unitType: 'Each',
        shipToType: form.shipToType || defaultShippingDestination || 'designer_warehouse',
        shipToAddress: '',
        status: 'Draft',
        source: 'purchase_order',
      },
    ]);
  };

  const updateItem = (id, patch) => {
    setItems((prev) => prev.map((it) => (it.itemId === id ? { ...it, ...patch } : it)));
  };

  const removeItem = (id) => {
    setItems((prev) => prev.filter((it) => it.itemId !== id));
  };

  /* ----------------------------------------------------
     UNSAVED CHANGES
  ----------------------------------------------------- */

  // Nothing here is stored until Save, yet the status pill and every field
  // update as soon as they're edited — so leaving for another tab (Schedule,
  // to import this PO) used to drop the edit without a word. Compare what a
  // save would send against what was loaded or last saved. Until the user
  // edits something, the baseline follows the form, so settings, vendors and
  // defaults that fill fields in after the PO loads don't count as edits.
  const editSnapshot = JSON.stringify({ form, items, tags });
  const editSnapshotRef = useRef(editSnapshot);
  editSnapshotRef.current = editSnapshot;
  const savedSnapshotRef = useRef(editSnapshot);
  const userEditedRef = useRef(false);

  useEffect(() => {
    if (!userEditedRef.current) savedSnapshotRef.current = editSnapshot;
  }, [editSnapshot]);

  const markUserEdit = () => {
    userEditedRef.current = true;
  };

  const markSaved = (savedSnapshot) => {
    savedSnapshotRef.current = savedSnapshot;
    userEditedRef.current = editSnapshotRef.current !== savedSnapshot;
  };

  // A payment recorded, edited or deleted in the Payments panel (or a
  // QuickBooks sync or return) is stored by the server as it happens, so the
  // new total it hands back is saved, not an edit to warn about.
  const adoptSavedPayments = (payments) => {
    setForm((prev) => ({ ...prev, payments }));
    const baseline = JSON.parse(savedSnapshotRef.current);
    baseline.form.payments = payments;
    savedSnapshotRef.current = JSON.stringify(baseline);
  };

  const hasUnsavedChanges = useCallback(
    () => userEditedRef.current && editSnapshotRef.current !== savedSnapshotRef.current,
    []
  );

  const unsavedGuard = useUnsavedChangesGuard(hasUnsavedChanges);

  /* ----------------------------------------------------
     CATALOG
  ----------------------------------------------------- */

  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(null);
  const [q, setQ] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [qbVendors, setQbVendors] = useState([]);
  const [qbConnected, setQbConnected] = useState(true);
  const [vendorSelectValue, setVendorSelectValue] = useState('');

  const refreshCatalog = async () => {
    try {
      setCatalogLoading(true);
      const res = await fetch(`${API_BASE}/api/products/${userId}`, { cache: 'no-store' });
      const data = await res.json();

      const normalized = (Array.isArray(data) ? data : data.Items || data.items || []).map(
        (it) => ({
          itemId: it.itemId || it.id || genId(),
          name: it.name || '',
          description: it.description || '',
          imageUrl: it.imageUrl || '',
          category: it.category || '',
          salesCode: it.salesCode || '',
          vendorId: it.vendorId || null,
          vendorName: it.vendorName || it.vendor || '',
          type: it.type || (it.category === 'Service' ? 'service' : 'product'),
          tradePrice: num(it.tradePrice ?? it.price ?? 0),
          shipping: num(it.shipping ?? 0),
        })
      );

      setCatalog(normalized);
      setVendorOptions(
        Array.from(new Set(normalized.map((it) => it.vendorName).filter(Boolean))).sort()
      );
    } catch (e) {
      setCatalogError(e.message || 'Failed to load catalog');
    } finally {
      setCatalogLoading(false);
    }
  };

  useEffect(() => {
    refreshCatalog();
  }, []);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/vendors/${encodeURIComponent(userId)}`);
        const data = await res.json();
        setVendors(Array.isArray(data) ? data : []);
      } catch {
        setVendors([]);
      }
    })();
  }, [API_BASE, userId]);

  useEffect(() => {
    if (!form.vendorName) {
      setVendorSelectValue('');
      return;
    }

    const matchVendor = vendors.find(
      (v) =>
        v.vendorId === form.vendorName ||
        (v.name || '').toLowerCase() === form.vendorName.toLowerCase()
    );
    if (matchVendor?.vendorId) {
      setVendorSelectValue(`local:${matchVendor.vendorId}`);
      return;
    }

    if (vendorOptions.some((v) => v.toLowerCase() === form.vendorName.toLowerCase())) {
      setVendorSelectValue(`local:${form.vendorName}`);
      return;
    }

    setVendorSelectValue(`local:${form.vendorName}`);
  }, [form.vendorName, vendors, vendorOptions]);

  useEffect(() => {
    if (!form.vendorName) return;
    if (!vendors.length) return;

    const matchVendor = vendors.find(
      (v) =>
        v.vendorId === form.vendorName ||
        (v.name || '').toLowerCase() === form.vendorName.toLowerCase()
    );

    if (!matchVendor) return;

    setForm((f) => ({
      ...f,
      vendorName: matchVendor.name || f.vendorName,
      vendorId: matchVendor.vendorId || f.vendorId || '',
      vendorStreet: f.vendorStreet || matchVendor.streetAddress || '',
      vendorCity: f.vendorCity || matchVendor.city || '',
      vendorState: f.vendorState || matchVendor.state || '',
      vendorZip: f.vendorZip || matchVendor.zip || '',
      vendorPhone: f.vendorPhone || matchVendor.phone || '',
      vendorEmail: f.vendorEmail || matchVendor.email || '',
    }));
  }, [form.vendorName, vendors]);

  useEffect(() => {
    const token = localStorage.getItem('idToken');
    if (!token) {
      setQbConnected(false);
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/quickbooks/vendors`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 409) {
          setQbConnected(false);
          setQbVendors([]);
          return;
        }
        if (!res.ok) {
          setQbConnected(false);
          setQbVendors([]);
          return;
        }
        const data = await res.json();
        const normalized = Array.isArray(data)
          ? data.map((v) => ({
              id: v.Id,
              displayName:
                v.DisplayName ||
                v.CompanyName ||
                [v.GivenName, v.FamilyName].filter(Boolean).join(' ') ||
                'Vendor',
              companyName: v.CompanyName || '',
              firstName: v.GivenName || '',
              lastName: v.FamilyName || '',
              email: v.PrimaryEmailAddr?.Address || '',
              phone: v.PrimaryPhone?.FreeFormNumber || v.Mobile?.FreeFormNumber || '',
              billAddr: v.BillAddr || {},
            }))
          : [];
        setQbConnected(true);
        setQbVendors(normalized);
      } catch {
        setQbConnected(false);
        setQbVendors([]);
      }
    })();
  }, [API_BASE]);

  useEffect(() => {
    if (initial?.orderNumber) return;
    const projectId = project?.id ?? project?.projectId;
    if (!userId || !projectId) return;

    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/purchase-orders/latest?userId=${encodeURIComponent(
            userId
          )}&projectId=${encodeURIComponent(projectId)}`
        );
        if (!res.ok) return;
        const data = await res.json();
        if (data?.orderNumber) {
          setForm((f) => ({ ...f, orderNumber: data.orderNumber }));
        }
      } catch {}
    })();
  }, [API_BASE, userId, project, initial]);

  /* ----------------------------------------------------
     TOTALS (BACKEND-COMPATIBLE)
  ----------------------------------------------------- */

  const subtotal = useMemo(
    () => items.reduce((s, it) => s + num(it.rate) * num(it.quantity) + num(it.shipping), 0),
    [items]
  );

  const totals = useMemo(() => {
    const freight = num(form.freight);
    const tariff = num(form.tariff);
    const autoTaxes =
      form.taxes === '' || form.taxes === null
        ? (subtotal * num(form.taxRatePct)) / 100
        : num(form.taxes);
    const payments = num(form.payments);
    const total = subtotal + freight + tariff + autoTaxes;
    const balance = Math.max(0, total - payments);
    const depositRequestedPct = Math.max(0, Math.min(100, num(form.depositRequestedPct)));
    const depositRequestedAmount = (total * depositRequestedPct) / 100;

    return {
      subtotal,
      freight,
      tariff,
      taxes: autoTaxes,
      payments,
      total,
      balance,
      depositRequestedPct,
      depositRequestedAmount,
    };
  }, [subtotal, form.freight, form.tariff, form.taxes, form.taxRatePct, form.payments, form.depositRequestedPct]);

  /* ----------------------------------------------------
     SAVE PURCHASE ORDER
  ----------------------------------------------------- */

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const saveInFlightRef = useRef(false);
  const [syncingQB, setSyncingQB] = useState(false);
  const [showQbConnect, setShowQbConnect] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnForm, setReturnForm] = useState({ reason: '', vendorCreditAmount: '', items: [] });
  const [submittingReturn, setSubmittingReturn] = useState(false);
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitSelectedIds, setSplitSelectedIds] = useState([]);
  const [submittingSplit, setSubmittingSplit] = useState(false);
  const [sendPOModal, setSendPOModal] = useState({ open: false, recipient: 'vendor', email: '', url: '', recipientName: '' });

  // Adds a status typed on this form to the account-wide list. Re-reads
  // settings first so a status added elsewhere (another tab, the settings page)
  // since this form loaded isn't dropped by writing back a stale array.
  const addCustomOrderStatus = async (label) => {
    const res = await fetch(`${API_BASE}/api/settings/${encodeURIComponent(userId)}`);
    const settings = res.ok ? await res.json().catch(() => ({})) : {};
    const current = normalizeCustomStatuses(settings?.customOrderStatuses);
    const updated = current.some((s) => s.toLowerCase() === label.toLowerCase())
      ? current
      : [...current, label];

    const saveRes = await fetch(`${API_BASE}/api/settings/${encodeURIComponent(userId)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customOrderStatuses: updated }),
    });
    if (!saveRes.ok) throw new Error('Failed to save status.');
    setCustomOrderStatuses(updated);
  };

  const [itemStatusModalId, setItemStatusModalId] = useState(null);
  const [itemStatusForm, setItemStatusForm] = useState(null);

  const openItemStatusModal = (itemId) => {
    const it = items.find((x) => x.itemId === itemId);
    setItemStatusForm({
      status: it?.status || 'Draft',
      orderedDate: it?.orderedDate || '',
      backorderedDate: it?.backorderedDate || '',
      enRouteDate: it?.enRouteDate || '',
      freightTracking: it?.freightTracking || '',
      receivedDate: it?.receivedDate || '',
      warehouseReceiptNumber: it?.warehouseReceiptNumber || '',
      deliveredDate: it?.deliveredDate || '',
      installedDate: it?.installedDate || '',
    });
    setItemStatusModalId(itemId);
  };

  const closeItemStatusModal = () => {
    setItemStatusModalId(null);
    setItemStatusForm(null);
  };

  const saveItemStatusModal = () => {
    updateItem(itemStatusModalId, itemStatusForm);
    closeItemStatusModal();
  };

  async function saveDraft(stayOpen = false) {
    if (saveInFlightRef.current) return null;
    if (!userId) return setError('Missing userId.');
    if (!project?.id && !project?.projectId) return setError('Missing projectId.');
    if (!form.orderNumber.trim()) return setError('Order number is required.');
    if (!form.vendorName.trim()) return setError('Vendor name is required.');
    if (items.length === 0) return setError('Add at least one item.');

    saveInFlightRef.current = true;
    setSaving(true);
    setError(null);

    const snapshotAtSave = editSnapshotRef.current;
    const persistedPayments = num(savedPO?.payments ?? initial?.payments);
    const effectivePayments = Math.max(totals.payments, persistedPayments);
    const payload = {
      userId,
      projectId: project?.id ?? project?.projectId,
      poId: initial?.poId,
      proposalId: initial?.proposalId || initial?.sourceProposalId || null,
      invoiceId: initial?.invoiceId || initial?.sourceInvoiceId || null,
      ...form,
      // Clear Ship To on the record itself for Will Call, so a leftover address
      // from an earlier destination can't reach the vendor-facing PO.
      ...(willCall && BLANK_SHIP_TO),
      tags,
      items: items.map((it) => ({
        itemId: it.itemId,
        productItemId: it.productItemId || it.itemId || null,
        vendorId: it.vendorId || null,
        type: it.type || '',
        category: it.category || '',
        salesCode: it.salesCode || '',
        name: it.name,
        description: it.description,
        sourceDescription: it.sourceDescription || '',
        imageUrl: it.imageUrl,
        vendorName: it.vendorName,
        room: it.room || '',
        scheduleId: it.scheduleId || '',
        rate: num(it.rate),
        shipping: num(it.shipping ?? 0),
        quantity: num(it.quantity),
        unitType: it.unitType || 'Each',
        shipToType: it.shipToType || '',
        shipToAddress: it.shipToAddress || '',
        status: it.status,
        orderedDate: it.orderedDate || '',
        backorderedDate: it.backorderedDate || '',
        enRouteDate: it.enRouteDate || '',
        freightTracking: it.freightTracking || '',
        receivedDate: it.receivedDate || '',
        warehouseReceiptNumber: it.warehouseReceiptNumber || '',
        deliveredDate: it.deliveredDate || '',
        installedDate: it.installedDate || '',
      })),
      subtotal: totals.subtotal,
      freight: totals.freight,
      tariff: totals.tariff,
      taxes: totals.taxes,
      depositRequestedPct: totals.depositRequestedPct,
      payments: effectivePayments,
      paymentHistory: savedPO?.paymentHistory || initial?.paymentHistory || [],
      total: totals.total,
      balance: Math.max(0, totals.total - effectivePayments),
    };

    try {
      const existingPoId = savedPO?.poId || initial?.poId;
      const url = existingPoId
        ? `${API_BASE}/api/purchase-orders/${userId}/by-id/${existingPoId}`
        : `${API_BASE}/api/purchase-orders`;
      const method = existingPoId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error('Failed to save purchase order.');
      const saved = await res.json();
      markSaved(snapshotAtSave);
      setSavedPO(saved);
      setForm((prev) => ({
        ...prev,
        payments: saved?.payments ?? prev.payments,
      }));

      const savedPoId = saved?.poId || existingPoId || payload.poId;
      // Awaited so a sync failure surfaces on *this* PO before the user (or
      // a "Save & Close" queue advance) moves on to the next one — a
      // fire-and-forget sync here previously let failures go unnoticed
      // whenever the form navigated away before it settled.
      if (
        qbConnected &&
        shouldAutoCreateInQuickBooks({ quickbooks: quickbooksSettings }) &&
        savedPoId &&
        payload.projectId
      ) {
        const qbSync = saved?.quickbooks?.billId
          ? autoUpdateQuickBooksPurchaseOrder({ poId: savedPoId, projectId: payload.projectId })
          : autoCreateQuickBooksPurchaseOrder({ poId: savedPoId, projectId: payload.projectId });

        try {
          const qbSyncResult = await qbSync;
          if (qbSyncResult?.warning) {
            // Synced, but part of the export (usually payments blocked by closed
            // books) did not land. A toast rather than setError, because the form
            // may close on save and take the inline message with it.
            notify.warning(qbSyncResult.warning, { duration: 12000 });
          }
          const r = await fetch(
            `${API_BASE}/api/purchase-orders/${encodeURIComponent(userId)}/by-id/${encodeURIComponent(savedPoId)}`,
            { cache: 'no-store' }
          );
          const refreshed = r.ok ? await r.json() : null;
          if (refreshed) {
            setSavedPO(refreshed);
            setForm((prev) => ({ ...prev, payments: refreshed?.payments ?? prev.payments }));
          }
        } catch (qbErr) {
          setError(
            `Purchase order saved, but QuickBooks auto-sync failed: ${
              qbErr.message || 'Unable to sync purchase order.'
            }`
          );
          // Don't advance the queue / close on a failed sync — leave the
          // user looking at the PO that actually failed so it isn't lost.
          return saved;
        }
      }

      if (!stayOpen) onSaved?.(saved);
      return saved;
    } catch (e) {
      setError(e.message || 'Failed to save purchase order');
    } finally {
      setSaving(false);
      saveInFlightRef.current = false;
    }
  }

  const emailVendor = async () => {
    const saved = await saveDraft(true);
    if (!saved?.poId) return setError("We couldn't save this document. Please try again.");
    setSendPOModal({
      open: true,
      recipient: 'vendor',
      email: saved.vendorEmail || form.vendorEmail || '',
      recipientName: saved.vendorName || form.vendorName || '',
      url: buildPublicPurchaseOrderVendorUrl(window.location.origin, saved.vendorShareToken),
    });
  };

  const emailClient = async () => {
    const saved = await saveDraft(true);
    if (!saved?.poId) return setError("We couldn't save this document. Please try again.");
    const primaryEmail = project?.email || '';
    setSendPOModal({
      open: true,
      recipient: 'client',
      email: [...new Set([primaryEmail, project?.secondaryEmail].filter(Boolean))].join(', '),
      recipientName: project?.customerName || project?.projectNickname || project?.name || '',
      url: buildPublicPurchaseOrderClientUrl(window.location.origin, saved.clientShareToken),
    });
  };

  /* ----------------------------------------------------
     RENDER
  ----------------------------------------------------- */

  const refreshPO = async () => {
    if (!savedPO?.poId || !userId) return;
    try {
      const res = await fetch(
        `${API_BASE}/api/purchase-orders/${encodeURIComponent(userId)}/by-id/${encodeURIComponent(savedPO.poId)}`,
        {
          cache: 'no-store',
        }
      );
      if (!res.ok) return;
      const data = await res.json();
      setSavedPO(data);
      if (data?.payments != null) adoptSavedPayments(data.payments);
    } catch {}
  };

  async function createQuickBooksPurchaseOrder() {
    const token = localStorage.getItem('idToken');
    if (!token) {
      setError('You must be signed in to sync with QuickBooks.');
      return;
    }

    setSyncingQB(true);
    setError(null);

    try {
      const persisted = savedPO?.poId ? savedPO : await saveDraft(true);
      const persistedPoId = persisted?.poId || savedPO?.poId || initial?.poId;
      const persistedProjectId = project?.id ?? project?.projectId;

      if (!persistedPoId || !persistedProjectId) {
        throw new Error('Save the purchase order before syncing to QuickBooks.');
      }

      const res = await fetch(`${API_BASE}/api/quickbooks/create-purchase-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          poId: persistedPoId,
          projectId: persistedProjectId,
        }),
      });

      const rawBody = await res.text();
      let data = {};
      try {
        data = rawBody ? JSON.parse(rawBody) : {};
      } catch {
        data = { error: rawBody || null };
      }
      if (isQuickBooksConnectionError(data)) {
        setShowQbConnect(true);
        return;
      }

      if (!res.ok) {
        const message =
          data?.error ||
          data?.details?.error ||
          data?.details?.Fault?.Error?.[0]?.Detail ||
          rawBody ||
          'Failed to create QuickBooks bill.';
        const alreadySyncedBill = /already synced as a quickbooks bill/i.test(message);
        if (alreadySyncedBill) {
          const updateRes = await fetch(`${API_BASE}/api/quickbooks/update-purchase-order`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              poId: persistedPoId,
              projectId: persistedProjectId,
            }),
          });

          const updateRaw = await updateRes.text();
          let updateData = {};
          try {
            updateData = updateRaw ? JSON.parse(updateRaw) : {};
          } catch {
            updateData = { error: updateRaw || null };
          }

          if (!updateRes.ok) {
            throw new Error(
              updateData?.error ||
                updateData?.details?.error ||
                updateData?.details?.Fault?.Error?.[0]?.Detail ||
                updateRaw ||
                'Failed to update QuickBooks bill.'
            );
          }
          data = updateData;
        } else {
          throw new Error(message);
        }
      }

      if (data?.quickbooksUrl) {
        window.open(data.quickbooksUrl, '_blank', 'noopener,noreferrer');
      }
      await refreshPO();
      if (data?.warning) {
        // The bill reached QuickBooks but something inside it (usually payments
        // blocked by closed books) did not — keep that on screen instead of
        // reporting a clean success.
        setError(data.warning);
        notify.warning('QuickBooks bill synced with warnings.');
      } else if (data?.billId) {
        notify('QuickBooks bill synced.');
      } else {
        notify('QuickBooks sync completed.');
      }
    } catch (e) {
      setError(e.message || 'Failed to sync purchase order as a QuickBooks bill.');
    } finally {
      setSyncingQB(false);
    }
  }

  const downloadPDF = async () => {
    if (!pdfDocumentRef.current) return;
    const el = pdfDocumentRef.current;
    // html2pdf measures page breaks before rendering, so the logo and
    // thumbnails must already be loaded or the layout shifts underneath it.
    const imgs = Array.from(el.querySelectorAll('img'));
    await Promise.all(
      imgs.map((img) =>
        img.complete
          ? Promise.resolve()
          : new Promise((resolve) => {
              img.addEventListener('load', resolve, { once: true });
              img.addEventListener('error', resolve, { once: true });
            })
      )
    );
    html2pdf()
      .set({
        margin: [12, 12, 12, 12],
        filename: `PO-${form.orderNumber || savedPO?.poId || 'purchase-order'}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: '#fff' },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'] },
      })
      .from(el)
      .save();
  };

  const autoDownloadDoneRef = useRef(false);
  useEffect(() => {
    if (!autoDownload || !settingsLoaded || autoDownloadDoneRef.current || !pdfDocumentRef.current) return;
    autoDownloadDoneRef.current = true;
    downloadPDF();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoDownload, settingsLoaded]);

  const companyAddressLines = splitLines(company.address);
  const vendorAddressLines = [
    form.vendorStreet,
    [form.vendorCity, form.vendorState, form.vendorZip]
      .filter(Boolean)
      .join(', ')
      .replace(', ,', ','),
  ].filter(Boolean);
  const shipToLines = [
    form.shipStreet,
    [form.shipCity, form.shipState, form.shipZip].filter(Boolean).join(', ').replace(', ,', ','),
  ].filter(Boolean);
  const printableItems = items.map((it) => ({
    ...it,
    lineTotal: num(it.rate) * num(it.quantity) + num(it.shipping ?? 0),
  }));

  return (
    <div className="doc-page po-form" onChangeCapture={markUserEdit} onClickCapture={markUserEdit}>
      <div className="po-pdf-render-root" aria-hidden="true">
        <div className="po-print-sheet" ref={pdfDocumentRef}>

          {/* ── HEADER: logo + company left, PO # right ── */}
          <header className="po-print-header">
            <div className="po-print-brand">
              {company.logoUrl ? (
                <img src={proxyImg(company.logoUrl)} alt="Company logo" className="po-print-logo" crossOrigin="anonymous" />
              ) : null}
              <div className="po-print-company-lines">
                {company.companyName && <div className="po-print-company-name">{company.companyName}</div>}
                {companyAddressLines.map((line) => (
                  <div key={line} className="po-print-company-line">{line}</div>
                ))}
                {company.contactNumber && <div className="po-print-company-line">{company.contactNumber}</div>}
                {company.email && <div className="po-print-company-line">{company.email}</div>}
                {company.website && <div className="po-print-company-line">{company.website}</div>}
              </div>
            </div>
            <div>
              <div className="po-print-doc-number">
                Purchase Order #{form.orderNumber || savedPO?.poId || ''}
              </div>
              {form.title && <div className="po-print-doc-title">{form.title}</div>}
            </div>
          </header>

          <div className="po-print-divider" />

          {/* ── ADDRESSES + DATES ── */}
          <div className="po-print-info-row">
            <div className="po-print-addresses">
              <div className="po-print-address-col">
                <div className="po-print-info-label">To:</div>
                <div className="po-print-info-name">{form.vendorName || 'Vendor Name'}</div>
                {vendorAddressLines.map((line) => (
                  <div key={line} className="po-print-info-line">{line}</div>
                ))}
                {form.vendorPhone && <div className="po-print-info-line">{form.vendorPhone}</div>}
              </div>
              <div className="po-print-address-col">
                <div className="po-print-info-label">Ship To:</div>
                {willCall ? (
                  <div className="po-print-info-name">Will Call</div>
                ) : (
                  <>
                    <div className="po-print-info-name">
                      {form.shipCompany || project?.customerName || `${project?.customerFirst || ''} ${project?.customerLast || ''}`.trim() || 'Recipient'}
                    </div>
                    {shipToLines.map((line) => (
                      <div key={line} className="po-print-info-line">{line}</div>
                    ))}
                    {(form.shipPhone || form.billToPhone) && (
                      <div className="po-print-info-line">{form.shipPhone || form.billToPhone}</div>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="po-print-meta-block">
              <div className="po-print-meta-row">
                <span className="po-print-meta-label">Order Date:</span>
                <span>{fmtDate(form.date)}</span>
              </div>
              <div className="po-print-meta-row">
                <span className="po-print-meta-label">Rep:</span>
                <span>{company.companyName || ''}</span>
              </div>
              {company.email && (
                <div className="po-print-meta-row">
                  <span className="po-print-meta-label">Email:</span>
                  <span>{company.email}</span>
                </div>
              )}
              {company.contactNumber && (
                <div className="po-print-meta-row">
                  <span className="po-print-meta-label">Phone:</span>
                  <span>{company.contactNumber}</span>
                </div>
              )}
            </div>
          </div>

          <div className="po-print-divider" />

          {(form.sidemark || project?.projectNickname || project?.name) && (
            <div className="po-print-sidemark">
              Sidemark:{' '}
              <strong>{form.sidemark || project?.projectNickname || project?.name || ''}</strong>
            </div>
          )}

          <table className="po-print-table">
            <thead>
              <tr>
                <th className="image">Image</th>
                <th className="qty">Quantity</th>
                <th className="unit">Unit</th>
                <th>Description</th>
                <th className="money">Unit Cost</th>
                <th className="money">Total Cost</th>
              </tr>
            </thead>
            <tbody>
              {printableItems.length ? (
                printableItems.map((it) => (
                  <tr key={it.itemId}>
                    <td className="image">
                      {it.imageUrl ? (
                        <img
                          src={proxyImg(it.imageUrl)}
                          alt={it.name || 'Line item'}
                          className="po-print-thumb"
                          crossOrigin="anonymous"
                        />
                      ) : (
                        <div className="po-print-thumb po-print-thumb-empty" />
                      )}
                    </td>
                    <td className="qty">{fmt2(it.quantity || 0)}</td>
                    <td className="unit">{it.unitType || 'Each'}</td>
                    <td>
                      <div className="po-print-item-room">{it.room || form.sidemark || ''}</div>
                      <div className="po-print-item-name">{it.name || 'Item'}</div>
                      {splitLines(it.description).map((line) => (
                        <div key={`${it.itemId}-${line}`} className="po-print-item-sub">
                          {line}
                        </div>
                      ))}
                      {/* Line-item status is internal tracking — deliberately
                          kept off the vendor-facing PDF. */}
                    </td>
                    <td className="money">{fmtMoney(it.rate)}</td>
                    <td className="money">{fmtMoney(it.lineTotal)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="po-print-empty-row">
                    No items
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="po-print-summary">
            <div className="po-print-summary-sep" />
            <div className="po-print-summary-row">
              <span>Sub Total</span>
              <span>{fmtMoney(totals.subtotal)}</span>
            </div>
            <div className="po-print-summary-row">
              <span>Freight</span>
              <span>{fmtMoney(totals.freight)}</span>
            </div>
            <div className="po-print-summary-row">
              <span>Tariff</span>
              <span>{fmtMoney(totals.tariff)}</span>
            </div>
            <div className="po-print-summary-row">
              <span>Sales Tax</span>
              <span>{fmtMoney(totals.taxes)}</span>
            </div>
            <div className="po-print-summary-row po-print-summary-strong">
              <span>Total</span>
              <span>{fmtMoney(totals.total)}</span>
            </div>
            <div className="po-print-summary-row">
              <span>Deposit Requested ({fmt2(totals.depositRequestedPct)}%)</span>
              <span>{fmtMoney(totals.depositRequestedAmount)}</span>
            </div>
            <div className="po-print-summary-row">
              <span>Payments</span>
              <span>{fmtMoney(totals.payments)}</span>
            </div>
            <div className="po-print-summary-sep po-print-summary-sep--brand" />
            <div className="po-print-summary-row po-print-summary-balance">
              <span>Balance</span>
              <span>{fmtMoney(totals.balance)}</span>
            </div>
          </div>

          <div className="po-print-footer">Purchase Order provided by InteriorFolio</div>
        </div>
      </div>

      <div>
        <HeaderSection
          proposalNumber={form.orderNumber}
          title={form.title}
          onClose={onClose}
          docType="purchase_order"
        />

        <div className="if-card po-header-card">
          <div className="po-header-left">
            <div className="po-doc-title">Purchase Order</div>
            <div className="po-doc-sub">
              {project?.projectNickname || project?.name || 'Project'} · {form.date}
            </div>
          </div>
          {queueTotal > 1 && queuePosition && (
            <div className="po-status-pill status-draft no-pdf">
              <span className="dot" />
              Purchase Order {queuePosition} of {queueTotal}
            </div>
          )}
          <div
            className={`po-status-pill status-${form.status.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <span className="dot" />
            {form.status}
          </div>
        </div>

        <div className="if-card">
          <div className="po-section-title">Order Details</div>
          <div className="po-grid four">
            <div className="po-field">
              <label>Order #</label>
              <input
                className="if-input"
                name="orderNumber"
                value={form.orderNumber}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, orderNumber: e.target.value }))}
              />
            </div>
            <div className="po-field">
              <label>Date</label>
              <input
                className="if-input"
                type="date"
                name="date"
                value={form.date}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              />
            </div>
            <div className="po-field">
              <label>Status</label>
              <StatusSelect
                className="if-select"
                value={form.status}
                builtInStatuses={BUILT_IN_PO_STATUSES}
                customStatuses={customOrderStatuses}
                disabled={!canEdit}
                onChange={(status) => setForm((f) => ({ ...f, status }))}
                onAddStatus={addCustomOrderStatus}
              />
            </div>
            <div className="po-field">
              <label>Sidemark</label>
              <input
                className="if-input"
                name="sidemark"
                value={form.sidemark}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, sidemark: e.target.value }))}
              />
            </div>
            <div className="po-field col-3">
              <label>Tracking URL</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  className="if-input"
                  name="trackingUrl"
                  value={form.trackingUrl}
                  disabled={!canEdit}
                  onChange={(e) => setForm((f) => ({ ...f, trackingUrl: e.target.value }))}
                  placeholder="https://carrier.com/track/..."
                />
                <button
                  type="button"
                  className="if-btn-secondary"
                  onClick={() => {
                    if (!form.trackingUrl) return;
                    window.open(form.trackingUrl, '_blank');
                  }}
                  disabled={!form.trackingUrl}
                >
                  Open
                </button>
              </div>
            </div>
            <div className="po-field">
              <label>Name (optional)</label>
              <input
                className="if-input"
                name="title"
                value={form.title}
                maxLength={DOCUMENT_TITLE_MAX_LENGTH}
                placeholder="e.g. Living Room"
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              />
            </div>
          </div>
        </div>

        <div className="if-card">
          <div className="po-section-title">Delivery &amp; Payment Terms</div>
          <div className="po-grid three">
            <div className="po-field">
              <label>Vendor Payment Terms</label>
              <input
                className="if-input"
                name="vendorPaymentTerms"
                value={form.vendorPaymentTerms}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, vendorPaymentTerms: e.target.value }))}
                placeholder="e.g. Net 30, CIA, 50% deposit"
              />
            </div>
            <div className="po-field">
              <label>Deposit Due Date</label>
              <input
                className="if-input"
                type="date"
                name="depositDueDate"
                value={form.depositDueDate ? String(form.depositDueDate).slice(0, 10) : ''}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, depositDueDate: e.target.value }))}
              />
            </div>
            <div className="po-field">
              <label>Balance Due Date</label>
              <input
                className="if-input"
                type="date"
                name="balanceDueDate"
                value={form.balanceDueDate ? String(form.balanceDueDate).slice(0, 10) : ''}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, balanceDueDate: e.target.value }))}
              />
            </div>
            <div className="po-field">
              <label>Estimated Delivery Date</label>
              <input
                className="if-input"
                type="date"
                name="estimatedDeliveryDate"
                value={form.estimatedDeliveryDate ? String(form.estimatedDeliveryDate).slice(0, 10) : ''}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, estimatedDeliveryDate: e.target.value }))}
              />
            </div>
            <div className="po-field">
              <label>Confirmed Ship Date</label>
              <input
                className="if-input"
                type="date"
                name="confirmedShipDate"
                value={form.confirmedShipDate ? String(form.confirmedShipDate).slice(0, 10) : ''}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, confirmedShipDate: e.target.value }))}
              />
            </div>
            <div className="po-field">
              <label>Actual Delivery Date</label>
              <input
                className="if-input"
                type="date"
                name="actualDeliveryDate"
                value={form.actualDeliveryDate ? String(form.actualDeliveryDate).slice(0, 10) : ''}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, actualDeliveryDate: e.target.value }))}
              />
            </div>
          </div>
        </div>

        <div className="if-card">
          <div className="po-section-title">Vendor</div>
          <div className="po-grid three">
            <div className="po-field">
              <label>Vendor Name</label>
              {vendorOptions.length || vendors.length ? (
                <select
                  className="if-select"
                  name="vendorName"
                  value={vendorSelectValue}
                  disabled={!canEdit}
                  onChange={async (e) => {
                    const value = e.target.value;
                    setVendorSelectValue(value);

                    if (!value) {
                      setForm((f) => ({ ...f, vendorName: '', vendorId: '' }));
                      return;
                    }

                    if (value.startsWith('local:')) {
                      const id = value.replace('local:', '');
                      const selectedVendor = vendors.find((v) => v.vendorId === id);
                      if (selectedVendor) {
                        setForm((f) => ({
                          ...f,
                          vendorName: selectedVendor.name || '',
                          vendorId: selectedVendor.vendorId || '',
                          vendorStreet: selectedVendor.streetAddress || '',
                          vendorCity: selectedVendor.city || '',
                          vendorState: selectedVendor.state || '',
                          vendorZip: selectedVendor.zip || '',
                          vendorPhone: selectedVendor.phone || '',
                          vendorEmail: selectedVendor.email || '',
                        }));
                      } else {
                        setForm((f) => ({ ...f, vendorName: id, vendorId: '' }));
                      }
                      return;
                    }

                    if (value.startsWith('qb:')) {
                      const id = value.replace('qb:', '');
                      const qbVendor = qbVendors.find((v) => v.id === id);
                      if (!qbVendor) return;

                      const existing = vendors.find((v) => v.quickbooksVendorId === qbVendor.id);

                      if (existing) {
                        setForm((f) => ({
                          ...f,
                          vendorName: existing.name || qbVendor.displayName || '',
                          vendorId: existing.vendorId || '',
                          vendorStreet: existing.streetAddress || '',
                          vendorCity: existing.city || '',
                          vendorState: existing.state || '',
                          vendorZip: existing.zip || '',
                          vendorPhone: existing.phone || '',
                          vendorEmail: existing.email || '',
                        }));
                        return;
                      }

                      setForm((f) => ({
                        ...f,
                        vendorName: qbVendor.displayName || '',
                        vendorId: '',
                        vendorStreet: qbVendor.billAddr?.Line1 || '',
                        vendorCity: qbVendor.billAddr?.City || '',
                        vendorState: qbVendor.billAddr?.CountrySubDivisionCode || '',
                        vendorZip: qbVendor.billAddr?.PostalCode || '',
                        vendorPhone: qbVendor.phone || '',
                        vendorEmail: qbVendor.email || '',
                      }));

                      if (!userId) return;
                      try {
                        const res = await fetch(`${API_BASE}/api/vendors`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            userId,
                            name: qbVendor.displayName,
                            companyName: qbVendor.companyName || null,
                            firstName: qbVendor.firstName || null,
                            lastName: qbVendor.lastName || null,
                            email: qbVendor.email || null,
                            phone: qbVendor.phone || null,
                            streetAddress: qbVendor.billAddr?.Line1 || null,
                            city: qbVendor.billAddr?.City || null,
                            state: qbVendor.billAddr?.CountrySubDivisionCode || null,
                            zip: qbVendor.billAddr?.PostalCode || null,
                            quickbooksVendorId: qbVendor.id,
                          }),
                        });
                          if (res.ok) {
                            const data = await res.json();
                            setForm((f) => ({
                              ...f,
                              vendorId: data.vendorId || f.vendorId || '',
                            }));
                            setVendors((prev) => [
                              ...prev,
                              {
                              vendorId: data.vendorId,
                              name: qbVendor.displayName,
                              streetAddress: qbVendor.billAddr?.Line1 || null,
                              city: qbVendor.billAddr?.City || null,
                              state: qbVendor.billAddr?.CountrySubDivisionCode || null,
                              zip: qbVendor.billAddr?.PostalCode || null,
                              phone: qbVendor.phone || null,
                              email: qbVendor.email || null,
                              quickbooksVendorId: qbVendor.id,
                            },
                          ]);
                        }
                      } catch {}
                    }
                  }}
                >
                  <option value="">Select vendor</option>
                  {form.vendorName &&
                    !vendors.some(
                      (v) =>
                        v.vendorId === form.vendorName ||
                        (v.name || '').toLowerCase() === form.vendorName.toLowerCase()
                    ) &&
                    !vendorOptions.some(
                      (v) => v.toLowerCase() === form.vendorName.toLowerCase()
                    ) && <option value={`local:${form.vendorName}`}>{form.vendorName}</option>}
                  {vendors.length > 0 && (
                    <optgroup label="Saved Vendors">
                      {vendors.map((v) => (
                        <option key={v.vendorId} value={`local:${v.vendorId}`}>
                          {v.name || v.companyName || 'Vendor'}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {vendorOptions.length > 0 && (
                    <optgroup label="From Products">
                      {vendorOptions.map((v) => (
                        <option key={v} value={`local:${v}`}>
                          {v}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {qbVendors.length > 0 && (
                    <optgroup label="QuickBooks">
                      {qbVendors.map((v) => (
                        <option key={v.id} value={`qb:${v.id}`}>
                          {v.displayName}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              ) : (
                <input
                  className="if-input"
                  name="vendorName"
                  value={form.vendorName}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      vendorName: e.target.value,
                      vendorId: '',
                    }))
                  }
                />
              )}
            </div>
            <div className="po-field">
              <label>Phone</label>
              <input
                className="if-input"
                name="vendorPhone"
                value={form.vendorPhone}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, vendorPhone: e.target.value }))}
              />
            </div>
            <div className="po-field">
              <label>Email</label>
              <input
                className="if-input"
                type="email"
                name="vendorEmail"
                value={form.vendorEmail}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, vendorEmail: e.target.value }))}
              />
            </div>
            <div className="po-field">
              <label>&nbsp;</label>
              {canEdit && <button
                type="button"
                className="if-btn-secondary"
                onClick={async () => {
                  if (!userId || !form.vendorName.trim()) return;
                  const trimmedName = form.vendorName.trim();
                  const emailLower = (form.vendorEmail || '').trim().toLowerCase();
                  const phoneDigits = (form.vendorPhone || '').replace(/\D/g, '');

                  const localMatch = vendors.find((v) => {
                    if ((v.name || '').toLowerCase() === trimmedName.toLowerCase()) return true;
                    if (emailLower && (v.email || '').trim().toLowerCase() === emailLower) return true;
                    if (phoneDigits.length >= 7 && (v.phone || '').replace(/\D/g, '') === phoneDigits) return true;
                    return false;
                  });

                  // An existing vendor gets its address written back rather than
                  // being left as-is — otherwise the address typed here never
                  // reaches the vendor record, and the next PO starts blank again.
                  if (localMatch) {
                    const patch = {
                      name: trimmedName,
                      email: form.vendorEmail || null,
                      phone: form.vendorPhone || null,
                      streetAddress: form.vendorStreet || null,
                      city: form.vendorCity || null,
                      state: form.vendorState || null,
                      zip: form.vendorZip || null,
                    };

                    try {
                      const res = await fetch(
                        `${API_BASE}/api/vendors/${encodeURIComponent(localMatch.vendorId)}`,
                        {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            ...localMatch,
                            ...patch,
                            userId,
                          }),
                        }
                      );
                      if (!res.ok) {
                        const data = await res.json().catch(() => ({}));
                        notify.warning(data.error || `Couldn't update "${localMatch.name}".`);
                        return;
                      }
                      setVendors((prev) =>
                        prev.map((v) =>
                          v.vendorId === localMatch.vendorId ? { ...v, ...patch } : v
                        )
                      );
                      setForm((f) => ({ ...f, vendorId: localMatch.vendorId || f.vendorId || '' }));
                      notify(`Vendor "${trimmedName}" updated.`);
                    } catch {
                      notify.warning(`Couldn't update "${localMatch.name}".`);
                    }
                    return;
                  }

                  try {
                    const res = await fetch(`${API_BASE}/api/vendors`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        userId,
                        name: trimmedName,
                        email: form.vendorEmail || null,
                        phone: form.vendorPhone || null,
                        streetAddress: form.vendorStreet || null,
                        city: form.vendorCity || null,
                        state: form.vendorState || null,
                        zip: form.vendorZip || null,
                      }),
                    });
                    if (!res.ok) return;
                    const data = await res.json();
                    setForm((f) => ({
                      ...f,
                      vendorId: data.vendorId || f.vendorId || '',
                    }));

                    if (data.duplicate) {
                      notify.warning(`Vendor "${data.vendor?.name || trimmedName}" already exists — using the existing vendor.`);
                      setVendors((prev) =>
                        prev.some((v) => v.vendorId === data.vendorId) ? prev : [...prev, data.vendor]
                      );
                      return;
                    }

                    setVendors((prev) => [
                      ...prev,
                      {
                        vendorId: data.vendorId,
                        name: trimmedName,
                        email: form.vendorEmail || null,
                        phone: form.vendorPhone || null,
                        streetAddress: form.vendorStreet || null,
                        city: form.vendorCity || null,
                        state: form.vendorState || null,
                        zip: form.vendorZip || null,
                      },
                    ]);
                  } catch {}
                }}
              >
                Save Vendor
              </button>}
            </div>
            <div className="po-field col-3">
              <label>Street</label>
              <AddressAutocomplete
                className="if-input"
                name="vendorStreet"
                value={form.vendorStreet}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, vendorStreet: e.target.value }))}
                onSelect={({ street, city, state, zip }) =>
                  setForm((f) => ({ ...f, vendorStreet: street, vendorCity: city, vendorState: state, vendorZip: zip }))
                }
              />
            </div>
            <div className="po-field">
              <label>City</label>
              <input
                className="if-input"
                name="vendorCity"
                value={form.vendorCity}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, vendorCity: e.target.value }))}
              />
            </div>
            <div className="po-field">
              <label>State</label>
              <input
                className="if-input"
                name="vendorState"
                value={form.vendorState}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, vendorState: e.target.value }))}
              />
            </div>
            <div className="po-field">
              <label>Zip</label>
              <input
                className="if-input"
                name="vendorZip"
                value={form.vendorZip}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, vendorZip: e.target.value }))}
              />
            </div>
          </div>
          <div className="po-hint">
            Select a vendor from your Products &amp; Services list to auto-fill details, or type
            manually and click “Save Vendor.”
          </div>
          {!qbConnected && <div className="po-hint">Connect QuickBooks to load vendors.</div>}
        </div>

        <div className="if-card">
          <div className="po-section-title">Bill To</div>
          <div className="po-grid three">
            <div className="po-field">
              <label>Name</label>
              <input
                className="if-input"
                name="billToName"
                value={form.billToName}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, billToName: e.target.value }))}
              />
            </div>
            <div className="po-field">
              <label>Phone</label>
              <input
                className="if-input"
                name="billToPhone"
                value={form.billToPhone}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, billToPhone: e.target.value }))}
              />
            </div>
            <div className="po-field">
              <label>Email</label>
              <input
                className="if-input"
                type="email"
                name="billToEmail"
                value={form.billToEmail}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, billToEmail: e.target.value }))}
              />
            </div>
            <div className="po-field col-3">
              <label>Street</label>
              <AddressAutocomplete
                className="if-input"
                name="billToStreet"
                value={form.billToStreet}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, billToStreet: e.target.value }))}
                onSelect={({ street, city, state, zip }) =>
                  setForm((f) => ({ ...f, billToStreet: street, billToCity: city, billToState: state, billToZip: zip }))
                }
              />
            </div>
            <div className="po-field">
              <label>City</label>
              <input
                className="if-input"
                name="billToCity"
                value={form.billToCity}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, billToCity: e.target.value }))}
              />
            </div>
            <div className="po-field">
              <label>State</label>
              <input
                className="if-input"
                name="billToState"
                value={form.billToState}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, billToState: e.target.value }))}
              />
            </div>
            <div className="po-field">
              <label>Zip</label>
              <input
                className="if-input"
                name="billToZip"
                value={form.billToZip}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, billToZip: e.target.value }))}
              />
            </div>
          </div>
        </div>

        <div className="if-card">
          <div className="po-section-title">Ship To</div>
          <div className="po-grid three">
            <div className="po-field col-3">
              <label>Default Delivery Destination</label>
              <select
                className="if-select"
                value={form.shipToType || 'designer_warehouse'}
                disabled={!canEdit}
                onChange={(e) => applyShipToType(e.target.value)}
              >
                {getFreightDestinationOptions(freightDestinations).map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>
            {willCall ? (
              <div className="po-field col-3">
                <div className="po-hint">
                  Will Call — this order is picked up at the vendor, so no Ship To name or address
                  goes on the purchase order. The vendor-facing PO shows “Will Call” instead.
                </div>
              </div>
            ) : (
              <>
                <div className="po-field col-3">
                  <label>Company / Name</label>
                  <input
                    className="if-input"
                    name="shipCompany"
                    value={form.shipCompany}
                    disabled={!canEdit}
                    onChange={(e) => setForm((f) => ({ ...f, shipCompany: e.target.value }))}
                  />
                </div>
                <div className="po-field col-3">
                  <label>Street</label>
                  <AddressAutocomplete
                    className="if-input"
                    name="shipStreet"
                    value={form.shipStreet}
                    disabled={!canEdit}
                    onChange={(e) => setForm((f) => ({ ...f, shipStreet: e.target.value }))}
                    onSelect={({ street, city, state, zip }) =>
                      setForm((f) => ({ ...f, shipStreet: street, shipCity: city, shipState: state, shipZip: zip }))
                    }
                  />
                </div>
                <div className="po-field">
                  <label>City</label>
                  <input
                    className="if-input"
                    name="shipCity"
                    value={form.shipCity}
                    disabled={!canEdit}
                    onChange={(e) => setForm((f) => ({ ...f, shipCity: e.target.value }))}
                  />
                </div>
                <div className="po-field">
                  <label>State</label>
                  <input
                    className="if-input"
                    name="shipState"
                    value={form.shipState}
                    disabled={!canEdit}
                    onChange={(e) => setForm((f) => ({ ...f, shipState: e.target.value }))}
                  />
                </div>
                <div className="po-field">
                  <label>Zip</label>
                  <input
                    className="if-input"
                    name="shipZip"
                    value={form.shipZip}
                    disabled={!canEdit}
                    onChange={(e) => setForm((f) => ({ ...f, shipZip: e.target.value }))}
                  />
                </div>
                <div className="po-field">
                  <label>Phone</label>
                  <input
                    className="if-input"
                    name="shipPhone"
                    value={form.shipPhone}
                    disabled={!canEdit}
                    onChange={(e) => setForm((f) => ({ ...f, shipPhone: e.target.value }))}
                  />
                </div>
                <div className="po-field">
                  <label>Email</label>
                  <input
                    className="if-input"
                    type="email"
                    name="shipEmail"
                    value={form.shipEmail}
                    disabled={!canEdit}
                    onChange={(e) => setForm((f) => ({ ...f, shipEmail: e.target.value }))}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <div className="if-card">
          <div className="po-section-head">
            <div className="po-section-title">Line Items</div>
            {canEdit && (
              <button type="button" className="if-btn-secondary" onClick={addCustomLineItem}>
                + Add Custom Line
              </button>
            )}
          </div>
          <div className="po-items-wrap">
            {items.length === 0 ? (
              <div className="po-empty">
                Add items from the catalog, or add a custom line for handling, freight or other
                charges.
              </div>
            ) : (
              items.map((it) => (
                <LineItemCard
                  key={it.itemId}
                  it={it}
                  updateItem={updateItem}
                  removeItem={removeItem}
                  onOpenStatus={openItemStatusModal}
                  readOnly={!canEdit}
                  schedules={schedules}
                  freightDestinations={freightDestinations}
                  defaultShipToType={form.shipToType || defaultShippingDestination || 'designer_warehouse'}
                />
              ))
            )}
          </div>
        </div>

        {canEdit && (
          <CatalogSection
            catalog={catalog}
            q={q}
            setQ={setQ}
            selectedType={selectedType}
            setSelectedType={setSelectedType}
            catalogLoading={catalogLoading}
            catalogError={catalogError}
            addItemFromCatalog={addItemFromCatalog}
            setShowAddItemModal={setShowAddItemModal}
            fmtUSD={fmt2}
          />
        )}

        <div className="if-card po-summary-card">
          <div>
            <div className="po-section-title">Notes</div>
            <textarea
              className="if-textarea"
              value={form.notes}
              disabled={!canEdit}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Add internal notes for this purchase order..."
            />
            <div style={{ marginTop: '12px' }}>
              <div className="po-section-title">Tags</div>
              <TagSelector selected={tags} onChange={setTags} readOnly={!canEdit} />
            </div>
            <div style={{ marginTop: '12px' }}>
              <PurchaseOrderAttachments form={form} setForm={setForm} readOnly={!canEdit} />
            </div>
          </div>
          <div className="po-totals">
            <div className="po-section-title">Amounts</div>
            <div className="po-field">
              <label>Tax Rate %</label>
              <input
                className="if-input"
                type="number"
                step="0.01"
                value={form.taxRatePct}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, taxRatePct: e.target.value }))}
                placeholder="0"
              />
            </div>
            <div className="po-field">
              <label>Freight</label>
              <input
                className="if-input"
                type="number"
                step="0.01"
                value={form.freight}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, freight: e.target.value }))}
              />
            </div>
            <div className="po-field">
              <label>Tariff</label>
              <input
                className="if-input"
                type="number"
                step="0.01"
                value={form.tariff}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, tariff: e.target.value }))}
              />
            </div>
            <div className="po-field">
              <label>Taxes</label>
              <input
                className="if-input"
                type="number"
                step="0.01"
                value={form.taxes}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, taxes: e.target.value }))}
                placeholder={totals.taxes ? totals.taxes.toFixed(2) : '0.00'}
              />
            </div>
            <div className="po-field">
              <label>Payments</label>
              <input
                className="if-input"
                type="number"
                step="0.01"
                value={form.payments}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, payments: e.target.value }))}
              />
            </div>
            <div className="po-field">
              <label>Deposit Requested %</label>
              <input
                className="if-input"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={form.depositRequestedPct}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, depositRequestedPct: e.target.value }))}
              />
            </div>
            <div className="po-totals-row">
              <span>Subtotal</span>
              <span>${fmt2(totals.subtotal)}</span>
            </div>
            <div className="po-totals-row">
              <span>Total</span>
              <span>${fmt2(totals.total)}</span>
            </div>
            <div className="po-totals-row total">
              <span>Balance</span>
              <span>${fmt2(totals.balance)}</span>
            </div>
            <div className="po-totals-row">
              <span>Deposit Requested</span>
              <span>${fmt2(totals.depositRequestedAmount)}</span>
            </div>
          </div>
        </div>

        <div className="po-actions no-pdf">
          <button className="if-btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          {savedPO?.poId && (
            <button type="button" className="if-btn-secondary" onClick={emailVendor} disabled={saving}>
              Send to Vendor
            </button>
          )}
          {savedPO?.poId && (
            <button type="button" className="if-btn-secondary" onClick={emailClient} disabled={saving}>
              Send to Client
            </button>
          )}
          {savedPO?.poId && (
            <button
              type="button"
              className="if-btn-secondary"
              onClick={() => {
                setReturnForm({ reason: '', vendorCreditAmount: '', items: items.map((it) => ({ itemId: it.itemId, itemName: it.name, qty: 0, maxQty: num(it.quantity) })) });
                setShowReturnModal(true);
              }}
            >
              Record Return
            </button>
          )}
          {savedPO?.poId && items.length > 1 && num(savedPO?.payments ?? initial?.payments) === 0 && (
            <button
              type="button"
              className="if-btn-secondary"
              onClick={async () => {
                // The split works on the stored PO, so store pending edits
                // first — a status change made just before splitting would
                // otherwise be dropped from both POs.
                if (hasUnsavedChanges() && !(await saveDraft(true))) return;
                setSplitSelectedIds([]);
                setShowSplitModal(true);
              }}
            >
              Split Purchase Order
            </button>
          )}
          {canEdit && (
            <button className="if-btn-secondary" onClick={() => saveDraft(true)} disabled={saving}>
              {saving ? 'Saving…' : 'Save Draft'}
            </button>
          )}
          {canEdit && (
            <button className="if-btn-primary" onClick={() => saveDraft(false)} disabled={saving}>
              {saving ? 'Saving…' : queueRemaining > 0 ? 'Save & Next' : 'Save & Close'}
            </button>
          )}
          {qbBillUrl && (
            <a
              className="if-btn-success"
              href={qbBillUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open QB Bill
            </a>
          )}
          {qbCompanionPoUrl && (
            <a
              className="if-btn-success"
              href={qbCompanionPoUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open QB PO
            </a>
          )}
          <button
            className="if-btn-qb"
            onClick={createQuickBooksPurchaseOrder}
            disabled={saving || syncingQB}
          >
            {syncingQB ? 'Syncing…' : 'Sync QuickBooks Bill'}
          </button>
          <button className="if-btn-secondary" onClick={downloadPDF}>
            Download PDF
          </button>
        </div>

        {error && <div className="po-error">{error}</div>}

        {savedPO?.poId && (
          <PurchaseOrderPayments
            purchaseOrder={savedPO}
            projectId={project?.id ?? project?.projectId ?? ''}
            onUpdated={refreshPO}
          />
        )}
        <QuickBooksConnectModal
          open={showQbConnect}
          onClose={() => setShowQbConnect(false)}
          onConnected={createQuickBooksPurchaseOrder}
          mode="invoice"
          message="To create QuickBooks bills from purchase orders, connect your QuickBooks account first."
        />

        {showReturnModal && (
          <div
            className="modal-overlay"
            onClick={() => setShowReturnModal(false)}
            style={{ opacity: 1, pointerEvents: 'auto' }}
          >
            <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
              <div className="modal-header">
                <h3>Record Vendor Return</h3>
                <button className="modal-close" onClick={() => setShowReturnModal(false)}>✕</button>
              </div>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div className="po-field">
                  <label>Reason</label>
                  <input
                    className="if-input"
                    value={returnForm.reason}
                    onChange={(e) => setReturnForm((f) => ({ ...f, reason: e.target.value }))}
                    placeholder="Damaged, wrong item, client rejection..."
                  />
                </div>
                <div className="po-field">
                  <label>Vendor Credit Amount ($)</label>
                  <input
                    className="if-input"
                    type="number"
                    step="0.01"
                    min="0"
                    value={returnForm.vendorCreditAmount}
                    onChange={(e) => setReturnForm((f) => ({ ...f, vendorCreditAmount: e.target.value }))}
                  />
                </div>
                <div>
                  <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>Items Being Returned</label>
                  {returnForm.items.map((it, idx) => (
                    <div key={it.itemId} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span style={{ flex: 1, fontSize: 13 }}>{it.itemName || it.itemId}</span>
                      <input
                        className="if-input"
                        type="number"
                        min="0"
                        max={it.maxQty}
                        step="1"
                        value={it.qty}
                        style={{ width: 70 }}
                        onChange={(e) =>
                          setReturnForm((f) => {
                            const updated = [...f.items];
                            updated[idx] = { ...updated[idx], qty: Number(e.target.value) };
                            return { ...f, items: updated };
                          })
                        }
                      />
                      <span style={{ fontSize: 12, color: '#888' }}>of {it.maxQty}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button className="if-btn-secondary" onClick={() => setShowReturnModal(false)}>Cancel</button>
                <button
                  className="if-btn-primary"
                  disabled={submittingReturn}
                  onClick={async () => {
                    const returnItems = returnForm.items.filter((it) => it.qty > 0);
                    if (returnItems.length === 0 && !returnForm.vendorCreditAmount) {
                      setError('Enter at least one item qty or a credit amount.');
                      return;
                    }
                    setSubmittingReturn(true);
                    try {
                      const projectId = project?.id ?? project?.projectId;
                      const res = await fetch(
                        `${API_BASE}/api/purchase-orders/${encodeURIComponent(projectId)}/${encodeURIComponent(savedPO.poId)}/return`,
                        {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('idToken')}` },
                          body: JSON.stringify({
                            returnItems,
                            vendorCreditAmount: num(returnForm.vendorCreditAmount),
                            reason: returnForm.reason,
                          }),
                        }
                      );
                      if (!res.ok) throw new Error('Failed to record return');
                      setShowReturnModal(false);
                      await refreshPO();
                      notify('Return recorded.');
                    } catch (e) {
                      setError(e.message || 'Failed to record return');
                    } finally {
                      setSubmittingReturn(false);
                    }
                  }}
                >
                  {submittingReturn ? 'Saving…' : 'Record Return'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showSplitModal && (
          <div
            className="modal-overlay"
            onClick={() => setShowSplitModal(false)}
            style={{ opacity: 1, pointerEvents: 'auto' }}
          >
            <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
              <div className="modal-header">
                <h3>Split Purchase Order</h3>
                <button className="modal-close" onClick={() => setShowSplitModal(false)}>✕</button>
              </div>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <p style={{ fontSize: 13, color: '#666' }}>
                  Select the items to move into a new purchase order. The remaining items stay on this PO.
                </p>
                <div>
                  {items.map((it) => {
                    const checked = splitSelectedIds.includes(it.itemId);
                    const lineTotal = num(it.rate) * num(it.quantity) + num(it.shipping);
                    return (
                      <label
                        key={it.itemId}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, cursor: 'pointer' }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            setSplitSelectedIds((prev) =>
                              e.target.checked
                                ? [...prev, it.itemId]
                                : prev.filter((id) => id !== it.itemId)
                            );
                          }}
                        />
                        <span style={{ flex: 1, fontSize: 13 }}>{it.name || 'Item'}</span>
                        <span style={{ fontSize: 12, color: '#888' }}>{fmtMoney(lineTotal)}</span>
                      </label>
                    );
                  })}
                </div>
                {splitSelectedIds.length > 0 && (
                  <div style={{ fontSize: 13, color: '#444' }}>
                    New PO: {splitSelectedIds.length} item{splitSelectedIds.length === 1 ? '' : 's'} · Remaining
                    PO: {items.length - splitSelectedIds.length} item
                    {items.length - splitSelectedIds.length === 1 ? '' : 's'}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="if-btn-secondary" onClick={() => setShowSplitModal(false)}>
                  Cancel
                </button>
                <button
                  className="if-btn-primary"
                  disabled={
                    submittingSplit ||
                    splitSelectedIds.length === 0 ||
                    splitSelectedIds.length === items.length
                  }
                  onClick={async () => {
                    setSubmittingSplit(true);
                    try {
                      const projectId = project?.id ?? project?.projectId;
                      const res = await fetch(
                        `${API_BASE}/api/purchase-orders/${encodeURIComponent(projectId)}/${encodeURIComponent(savedPO.poId)}/split`,
                        {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${localStorage.getItem('idToken')}`,
                          },
                          body: JSON.stringify({ itemIds: splitSelectedIds }),
                        }
                      );
                      const data = await res.json().catch(() => ({}));
                      if (!res.ok) throw new Error(data?.error || 'Failed to split purchase order');
                      setShowSplitModal(false);
                      notify(`Split complete — created ${data?.newPurchaseOrder?.orderNumber || 'a new PO'}.`);
                      // The split rewrote this PO on the server, so the form
                      // is stale — staying to save it would put the moved
                      // items back. Go to the new PO without asking.
                      userEditedRef.current = false;
                      navigate(`/projects/${projectId}/purchase-orders/${data.newPurchaseOrder.poId}`);
                    } catch (e) {
                      setError(e.message || 'Failed to split purchase order');
                    } finally {
                      setSubmittingSplit(false);
                    }
                  }}
                >
                  {submittingSplit ? 'Splitting…' : 'Split Purchase Order'}
                </button>
              </div>
            </div>
          </div>
        )}

        {itemStatusModalId && itemStatusForm && (
          <div
            className="modal-overlay"
            onClick={closeItemStatusModal}
            style={{ opacity: 1, pointerEvents: 'auto' }}
          >
            <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
              <div className="modal-header">
                <h3>Item Status</h3>
              </div>
              <div className="modal-body">
                <div className="po-field" style={{ marginBottom: 12 }}>
                  <label>Status</label>
                  <StatusSelect
                    className="if-select"
                    value={itemStatusForm.status}
                    builtInStatuses={BUILT_IN_PO_STATUSES}
                    customStatuses={customOrderStatuses}
                    disabled={!canEdit}
                    onChange={(status) => setItemStatusForm((f) => ({ ...f, status }))}
                    onAddStatus={addCustomOrderStatus}
                  />
                </div>
                <div className="po-grid two">
                  <div className="po-field">
                    <label>Ordered</label>
                    <input
                      className="if-input"
                      type="date"
                      disabled={!canEdit}
                      value={itemStatusForm.orderedDate}
                      onChange={(e) => setItemStatusForm((f) => ({ ...f, orderedDate: e.target.value }))}
                    />
                  </div>
                  <div className="po-field">
                    <label>Backordered</label>
                    <input
                      className="if-input"
                      type="date"
                      disabled={!canEdit}
                      value={itemStatusForm.backorderedDate}
                      onChange={(e) => setItemStatusForm((f) => ({ ...f, backorderedDate: e.target.value }))}
                    />
                  </div>
                  <div className="po-field">
                    <label>En Route</label>
                    <input
                      className="if-input"
                      type="date"
                      disabled={!canEdit}
                      value={itemStatusForm.enRouteDate}
                      onChange={(e) => setItemStatusForm((f) => ({ ...f, enRouteDate: e.target.value }))}
                    />
                  </div>
                  <div className="po-field">
                    <label>Freight Tracking</label>
                    <input
                      className="if-input"
                      type="text"
                      disabled={!canEdit}
                      value={itemStatusForm.freightTracking}
                      onChange={(e) => setItemStatusForm((f) => ({ ...f, freightTracking: e.target.value }))}
                    />
                  </div>
                  <div className="po-field">
                    <label>Received</label>
                    <input
                      className="if-input"
                      type="date"
                      disabled={!canEdit}
                      value={itemStatusForm.receivedDate}
                      onChange={(e) => setItemStatusForm((f) => ({ ...f, receivedDate: e.target.value }))}
                    />
                  </div>
                  <div className="po-field">
                    <label>Warehouse Receipt #</label>
                    <input
                      className="if-input"
                      type="text"
                      disabled={!canEdit}
                      value={itemStatusForm.warehouseReceiptNumber}
                      onChange={(e) =>
                        setItemStatusForm((f) => ({ ...f, warehouseReceiptNumber: e.target.value }))
                      }
                    />
                  </div>
                  <div className="po-field col-2">
                    <label>Delivered</label>
                    <input
                      className="if-input"
                      type="date"
                      disabled={!canEdit}
                      value={itemStatusForm.deliveredDate}
                      onChange={(e) => setItemStatusForm((f) => ({ ...f, deliveredDate: e.target.value }))}
                    />
                  </div>
                  <div className="po-field col-2">
                    <label>Installed</label>
                    <input
                      className="if-input"
                      type="date"
                      disabled={!canEdit}
                      value={itemStatusForm.installedDate}
                      onChange={(e) => setItemStatusForm((f) => ({ ...f, installedDate: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <button className="if-btn-secondary" onClick={closeItemStatusModal}>
                  Cancel
                </button>
                {canEdit && (
                  <button className="if-btn-primary" onClick={saveItemStatusModal}>
                    Save
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {sendPOModal.open && (
        <SendPurchaseOrderModal
          recipient={sendPOModal.recipient}
          defaultEmail={sendPOModal.email}
          recipientName={sendPOModal.recipientName}
          docNumber={form.orderNumber}
          docUrl={sendPOModal.url}
          companyName={company.companyName || project?.companyName || ''}
          onClose={() => setSendPOModal({ open: false, recipient: 'vendor', email: '', url: '', recipientName: '' })}
          onSent={() => notify(`Purchase order sent to ${sendPOModal.recipient === 'vendor' ? 'vendor' : 'client'}.`)}
        />
      )}

      {/* Portaled so it stacks above the open mobile sidebar, which is where
          a blocked sidebar link would otherwise leave it hidden. */}
      {createPortal(
        <ConfirmModal
          isOpen={unsavedGuard.isPrompting}
          title="Unsaved Changes"
          message="This purchase order has changes that haven't been saved. If you leave now, they will be lost."
          confirmLabel="Leave Without Saving"
          destructive={true}
          onConfirm={unsavedGuard.confirmLeave}
          onCancel={unsavedGuard.cancelLeave}
        />,
        document.body
      )}
    </div>
  );
}

PurchaseOrderForm.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
  project: PropTypes.object,
  initial: PropTypes.object,
  autoDownload: PropTypes.bool,
  queueRemaining: PropTypes.number,
  queuePosition: PropTypes.number,
  queueTotal: PropTypes.number,
};
